import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import { ResolvedRestaurant, UserRestaurantAccess, TenantRole } from './types'
import type { User, Session } from '@supabase/supabase-js'

// ─── Auth Hook ───────────────────────────────────────────────
export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s)
      setUser(s?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      setUser(s?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  return { user, session, loading, signIn, signOut }
}

// ─── Resolve Restaurant Hook ─────────────────────────────────
export function useResolvedRestaurant(slug: string | undefined) {
  const [restaurant, setRestaurant] = useState<ResolvedRestaurant | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      setLoading(false)
      return
    }

    async function resolve() {
      try {
        setLoading(true)
        setError(null)

        const { data, error: rpcError } = await supabase.rpc(
          'resolve_restaurant',
          { p_slug: slug },
        )

        if (rpcError) {
          console.error('[NOIZA][tenant] resolve_restaurant error:', rpcError)
          setError('restaurant_not_found')
          return
        }

        // RPC returns an array; take first row
        const row = Array.isArray(data) ? data[0] : data
        if (!row) {
          setError('restaurant_not_found')
          return
        }

        setRestaurant(row as ResolvedRestaurant)
      } catch (err) {
        console.error('[NOIZA][tenant] resolve_restaurant exception:', err)
        setError('restaurant_not_found')
      } finally {
        setLoading(false)
      }
    }

    resolve()
  }, [slug])

  return { restaurant, loading, error }
}

// ─── Platform Admin Check ─────────────────────────────────────
export async function checkPlatformAdmin(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('platform_admins')
      .select('user_id, role, is_active')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle()

    if (error) {
      console.warn(
        '[NOIZA][tenant] platform_admins query error:',
        error.message,
      )
      return false
    }

    const isSuperAdmin = !!data && data.role === 'super_admin'
    console.log('[NOIZA][tenant] Platform admin check:', {
      userId,
      found: !!data,
      role: data?.role,
      isSuperAdmin,
    })
    return isSuperAdmin
  } catch (err) {
    console.warn('[NOIZA][tenant] platform_admins exception:', err)
    return false
  }
}

// ─── Tenant Access Hook ──────────────────────────────────────
export function useTenantAccess(
  userId: string | undefined,
  restaurantId: string | undefined,
) {
  const [access, setAccess] = useState<UserRestaurantAccess | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId || !restaurantId) {
      setLoading(false)
      return
    }

    async function fetchAccess() {
      try {
        setLoading(true)
        setError(null)

        // Step 1: Check if user is a platform super_admin
        const isSuperAdmin = await checkPlatformAdmin(userId!)
        if (isSuperAdmin) {
          console.log('[NOIZA][tenant] ✅ Super admin — granting full access')
          setAccess({
            user_id: userId!,
            restaurant_id: restaurantId!,
            role: 'owner' as TenantRole,
            is_active: true,
          } as UserRestaurantAccess)
          return
        }

        // Step 2: Fall back to restaurant_users check
        const { data, error: queryError } = await supabase
          .from('v_user_restaurant_access')
          .select('*')
          .eq('user_id', userId)
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .single()

        if (queryError || !data) {
          console.error('[NOIZA][tenant] access query error:', queryError)
          setError('access_denied')
          return
        }

        setAccess(data as UserRestaurantAccess)
      } catch (err) {
        console.error('[NOIZA][tenant] access exception:', err)
        setError('access_denied')
      } finally {
        setLoading(false)
      }
    }

    fetchAccess()
  }, [userId, restaurantId])

  return { access, loading, error }
}

// ─── Role Helpers ────────────────────────────────────────────
const GLOBAL_ROLES: TenantRole[] = ['owner', 'admin', 'manager', 'caja']
const STATION_ROLES: TenantRole[] = ['cocina', 'pizzeria', 'bebidas']

export function isGlobalRole(role: TenantRole): boolean {
  return GLOBAL_ROLES.includes(role)
}

export function isStationRole(role: TenantRole): boolean {
  return STATION_ROLES.includes(role)
}

export function getDefaultRedirect(slug: string, role: TenantRole): string {
  switch (role) {
    case 'owner':
    case 'admin':
    case 'manager':
    case 'caja':
      return `/admin/${slug}`
    case 'cocina':
      return `/admin/${slug}/kds?station=cocina`
    case 'pizzeria':
      return `/admin/${slug}/kds?station=pizzeria`
    case 'bebidas':
      return `/admin/${slug}/kds?station=bebidas`
    default:
      return `/admin/${slug}`
  }
}

export function canAccessRoute(role: TenantRole, route: string): boolean {
  // Global roles can access everything
  if (isGlobalRole(role)) return true

  // Station roles can only access KDS
  if (isStationRole(role)) {
    return route.includes('/kds')
  }

  return false
}
